import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import { sendLog, LoggerStatusEnum } from 'ui-model';
import { map, remove, pickBy, identity, get } from 'lodash';
import request from 'utils/request';
import { isMaster } from 'utils/permissions';
import { quiteRequest, sleep } from 'utils/helpers';
import isValidMongoId from 'utils/isValidMongoId';
import storage from '@lib/services/storageService';
import {
  LOAD_ACCOUNTS,
  LOAD_DATA,
  LOAD_NOTIFICATIONS,
  CLEAR_UNREAD_SYSTEM_NOTIFICATIONS,
  ITEMS_PER_PAGE,
  SAVE_REFERRAL,
  SWITCH_ACCOUNT,
  WAITLIST,
  BROADCAST,
  GROUP,
  PROVIDER,
  STAFF,
  FACILITY,
  APPT_TYPE,
  APPT,
  REMINDER,
  REFERRAL,
  OUTBOUND_REFERRAL,
  FORM_TEMPLATE,
  FORM,
  OFFER,
  LUMABOT_FLOW,
  LUMABOT_FLOW_TEMPLATE,
} from './constants';
import {
  loadAccountsError,
  loadAccountsSuccess,
  loadDataSuccess,
  loadDataError,
  notificationsLoaded,
  notificationsLoadingError,
  systemUnreadNotificationsCleared,
  systemUnreadNotificationsClearingError,
  saveReferralSuccess,
  saveReferralError,
  switchAccountError,
} from './actions';

export function* loadAccounts() {
  try {
    let endpoint = '/organizations';
    const organizations = yield call(request, endpoint);

    if (organizations.length > 0) {
      const organization = organizations[0]._id;
      endpoint = `/organizations/${organization}/access`;
      const accounts = yield call(request, endpoint);

      yield put(loadAccountsSuccess(organization, accounts));
    } else {
      yield put(loadAccountsSuccess(false, false));
    }
  } catch (err) {
    sendLog('Error on Header/sagas.js loadAccounts', { errorStack: err }, LoggerStatusEnum.error);
    yield put(loadAccountsError(err));
  }
}

/**
 * @ deprecated
 */
function* handlePatientSearch(query) {
  const endpoint = query ? `/patients/search?type=fullSearch&value=${query}` : '/patients/recent';
  let loadedData = yield call(request, endpoint);

  if (!query) {
    // lookup patients
    const patientPromises = map(loadedData, (id) => id && quiteRequest(`/patients/${id}`));
    const patients = remove(yield all(patientPromises), undefined);
    loadedData = patients;
  }

  return loadedData.response ? loadedData.response : loadedData;
}

/**
 * @ deprecated
 */
function getResourceUrl(type, { data }) {
  switch (type) {
    case BROADCAST:
      return `/broadcasts/${data._id}`;
    case GROUP:
      return `/v2/settings/security-center/groups/${data._id}`;
    case WAITLIST:
      return `/waitlists/${data._id}/`;
    case PROVIDER:
      return `/v2/settings/practice-resources/providers/${data._id}`;
    case STAFF:
      return `/v2/settings/security-center/users/${data._id}`;
    case FACILITY:
      return `/v2/settings/practice-resources/facilities/${data._id}`;
    case APPT_TYPE:
      return `/v2/settings/practice-resources/appointment-types/${data._id}`;
    case APPT:
      return `/patients/${data.patient._id}/appointments?id=${data._id}`;
    case REMINDER:
      return `/patients/${data.patient._id}/reminders?id=${data._id}`;
    case REFERRAL:
      return `/inbound-referrals/${data._id}`;
    case OUTBOUND_REFERRAL:
      return `/outbound-referrals/${data._id}`;
    case FORM_TEMPLATE:
      return `/v2/form-builder/${data._id}`;
    case FORM:
      return `/patients/${data.patient._id}/formAnswers/${data._id}`;
    case OFFER:
      return `/offers/${data._id}`;
    case LUMABOT_FLOW: {
      const patientId = get(data, 'patient._id');
      return `/patients/${patientId}/formAnswers/${data._id}`;
    }
    case LUMABOT_FLOW_TEMPLATE:
      return `/v2/settings/utilities/lumabot-builder/${data._id}`;
    default:
      return '';
  }
}

/**
 * @ deprecated
 */
function* handleMongoIdSearch(query) {
  const endpoint = `/lookups/${query}?includeDeleted=true`;
  const response = yield call(request, endpoint);

  const resourceType = get(response, 'subType') || get(response, 'type');

  // If query id is of a patient, it should follow the PatientSearch flow
  // and to accomplish that we can simply return the data object inside an array
  if (resourceType === 'Patient') {
    return [get(response, 'data')];
  }

  const resourceUrl = getResourceUrl(resourceType, response);

  if (!resourceUrl) return;

  const providers = Array.isArray(get(response, 'data.provider'))
    ? map(get(response, 'data.provider'), (provider) => provider.name)
    : get(response, 'data.provider.name');

  return pickBy(
    {
      _id: get(response, 'data._id'),
      type: resourceType,
      queryType: 'mongoId',
      resourceUrl,
      providers,
      patient: get(response, 'data.patient.name'),
      patientFormTemplateName: get(response, 'data.patientFormTemplateName'),
      name: get(response, 'data.name') || get(response, 'data.title'),
      date: get(response, 'data.date'),
      status: get(response, 'data.status'),
    },
    identity,
  );
}

export function* onLoadData(action) {
  try {
    const { query } = action;

    const results = yield isValidMongoId(query) ? handleMongoIdSearch(query) : handlePatientSearch(query);

    yield put(loadDataSuccess(results));
  } catch (err) {
    sendLog('Error on Header/sagas.js onLoadData ', { errorStack: err }, LoggerStatusEnum.error);
    yield put(loadDataError(err));
  }
}

export function* onSaveReferral(action) {
  try {
    const body = action.referral;
    const endpoint = `/referrals/${body._id}`;
    const options = {
      method: 'put',
      body,
    };
    const res = yield call(request, endpoint, options);
    yield put(saveReferralSuccess(res, `Referral ${res.status} successfully`));
  } catch (err) {
    sendLog(
      `Error on Header/sagas.js onSaveReferral referral: ${action.referral._id}`,
      { errorStack: err, context: { referral: action.referral._id } },
      LoggerStatusEnum.error,
    );
    yield put(saveReferralError(err));
  }
}

export function* onLoadNotifications(action) {
  try {
    const page = typeof action.page === 'number' && action.page >= 0 ? action.page : 1;
    const endpoint = `/notifications?limit=${ITEMS_PER_PAGE}&page=${page}`;
    const notifications = yield call(request, endpoint);

    yield put(notificationsLoaded(notifications.response));
  } catch (err) {
    sendLog(
      `Error on Header/sagas.js onLoadNotifications for page: ${
        typeof action.page === 'number' && action.page >= 0 ? action.page : 1
      }`,
      { errorStack: err },
      LoggerStatusEnum.error,
    );
    yield put(notificationsLoadingError(err));
  }
}

export function* clearUnReadNotifications() {
  try {
    const options = {
      method: 'put',
    };
    yield call(request, '/notifications/clear', options);
    yield sleep(4000);
    yield put(systemUnreadNotificationsCleared());
  } catch (err) {
    sendLog('Error on Header/sagas.js cleanUnReadNotifications', { errorStack: err }, LoggerStatusEnum.error);
    yield put(systemUnreadNotificationsClearingError(err));
  }
}

export function* switchAccount({ organization, account }) {
  try {
    let endpoint = `/organizations/${organization}/users/${account}/switch`;
    const options = { method: 'post' };
    const { user, token } = yield call(request, endpoint, options);

    if (user && token) {
      storage.removeAuth();

      storage.setUser({ token });

      if (!isMaster(user)) {
        endpoint = `/users/${user.user}`;
        const master = yield call(request, endpoint);

        user.settings = master.settings;
        // TODO: find a better solution
        user.userSetting = user.setting;
        user.setting = master.setting;
      }

      storage.setUser({ ...user, token });
      window.location.href = '/';
    }
  } catch (err) {
    sendLog(
      `Error on Header/sagas.js switchAccount for organization: ${organization} and account: ${account}`,
      { errorStack: err },
      LoggerStatusEnum.error,
    );
    yield put(switchAccountError(err));
  }
}

export function* watchClearNotifications() {
  yield takeLatest(CLEAR_UNREAD_SYSTEM_NOTIFICATIONS, clearUnReadNotifications);
}

export function* watchLoadNotifications() {
  yield takeLatest(LOAD_NOTIFICATIONS, onLoadNotifications);
}

export function* watchLoadAccounts() {
  yield takeLatest(LOAD_ACCOUNTS, loadAccounts);
}

export function* watchLoadData() {
  yield takeLatest(LOAD_DATA, onLoadData);
}

export function* watchSaveReferral() {
  yield takeLatest(SAVE_REFERRAL, onSaveReferral);
}

export function* watchSwitchAccount() {
  yield takeLatest(SWITCH_ACCOUNT, switchAccount);
}

// TODO replace `yield fork` by `yield []`
// https://github.com/redux-saga/redux-saga/issues/760
export default function* appSaga() {
  yield fork(watchClearNotifications);
  yield fork(watchLoadAccounts);
  yield fork(watchLoadNotifications);
  yield fork(watchLoadData);
  yield fork(watchSaveReferral);
  yield fork(watchSwitchAccount);
}
